import * as Yup from 'yup'

import { SchemaAttributes, SchemaGroups } from '@/Data/typesV2'
import { getVisibleAttributes } from '@/utils/CRUD/read/schema'

const FLAG_NAME_TO_CHAR: Record<string, string> = {
  ignoreCase: 'i',
  multiline: 'm',
  dotAll: 's',
  unicode: 'u',
}

function buildBrowserRegExp(
  pattern: string,
  attributeName: string,
  namedFlags?: string[],
): RegExp | null {
  const unknown = (namedFlags ?? []).filter((name) => !FLAG_NAME_TO_CHAR[name])
  if (unknown.length > 0) {
    console.error(
      `Skipping client-side pattern validation for "${attributeName}": unsupported regular expression flags: ${unknown.join(', ')}.`,
    )
    return null
  }

  const flagChars = [
    ...new Set((namedFlags ?? []).map((name) => FLAG_NAME_TO_CHAR[name])),
  ].join('')

  try {
    return new RegExp(pattern, flagChars)
  } catch (error) {
    // Ignore incompatible backend patterns in the frontend to avoid crashing the app.
    console.error(
      `Skipping client-side pattern validation for "${attributeName}": invalid browser regular expression "${pattern}".`,
      error,
    )
    return null
  }
}

/**
 * Builds a Yup validation schema for visible attributes.
 * Only validates mandatory fields that are in the schema's attributeOrder.
 *
 * @param schemaGroups - The schema groups containing attributeOrder arrays
 * @param groupsOrder - The ordered list of group names
 * @param schemaAttributes - The full schema attributes object
 * @param t - Translation function for validation messages
 * @returns Yup object schema with validation rules for mandatory visible attributes
 */
export function buildValidationSchema(
  schemaGroups: SchemaGroups,
  groupsOrder: string[],
  schemaAttributes: SchemaAttributes,
  t: (key: string) => string,
): Yup.ObjectSchema<Record<string, unknown>> {
  const { visibleAttributes } = getVisibleAttributes(
    schemaGroups,
    groupsOrder,
    schemaAttributes,
  )

  const validationSchemaArray = visibleAttributes.map((attributeName) => {
    const attributeSchema = schemaAttributes?.[attributeName]
    const title = attributeSchema?.title
    const isRequired = attributeSchema?.mandatory
    const type = attributeSchema?.type
    const maxLength = attributeSchema?.maxLength
    const pattern = attributeSchema?.validation?.pattern
    const flags = attributeSchema?.validation?.flags

    if (!isRequired && !pattern) return {}

    if (type === 'boolean') {
      return {
        [attributeName]: Yup.boolean()
          .required(t('errors.validation.fieldRequired'))
          .label(title || attributeName),
      }
    }

    let stringSchema = Yup.string()

    if (isRequired) {
      stringSchema = stringSchema.required(t('errors.validation.fieldRequired'))
    }

    if (maxLength && maxLength > 0) {
      stringSchema = stringSchema.max(maxLength)
    }

    if (pattern) {
      const browserPattern = buildBrowserRegExp(pattern, attributeName, flags)

      if (browserPattern) {
        stringSchema = stringSchema
          .transform((v: string | null) => v ?? '')
          .matches(browserPattern, {
            message: t('errors.validation.invalidFormat'),
            excludeEmptyString: true,
          })
      }
    }

    return { [attributeName]: stringSchema.label(title || attributeName) }
  })

  return Yup.object(Object.assign({}, ...validationSchemaArray)).required()
}
